import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/app/Layout";
import AdminSubnav from "@/components/app/AdminSubnav";
import { CheckCircle2, Send, FolderOpen, Clock, Inbox, UserCheck, AlertTriangle, ShieldQuestion, Undo2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import AdminUserFilesModal from "@/components/app/AdminUserFilesModal";

const STATUS_LABELS = {
  ready_for_review: { label: "À envoyer au VP", color: "amber", icon: Clock },
  pending_vp: { label: "En attente du VP", color: "blue", icon: ShieldQuestion },
  vp_approved: { label: "Approuvé par VP", color: "green", icon: UserCheck },
  rejected_by_vp: { label: "Refusé par VP", color: "red", icon: AlertTriangle },
};

export default function AdminReview() {
  const [reports, setReports] = useState([]);
  const [vps, setVps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [filesUser, setFilesUser] = useState(null);
  const [rejectCtx, setRejectCtx] = useState(null);
  const [rejectComment, setRejectComment] = useState("");
  const [vpCtx, setVpCtx] = useState(null);
  const [vpId, setVpId] = useState("");
  const [vpNote, setVpNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [rep, vp] = await Promise.all([
        api.get("/admin/reports-pending"),
        api.get("/admin/vps"),
      ]);
      setReports(rep.data);
      setVps(vp.data);
    } catch {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => ({
    ready_for_review: reports.filter((r) => r.status === "ready_for_review"),
    pending_vp: reports.filter((r) => r.status === "pending_vp"),
    vp_approved: reports.filter((r) => r.status === "vp_approved"),
    rejected_by_vp: reports.filter((r) => r.status === "rejected_by_vp"),
  }), [reports]);

  const approve = async (r) => {
    if (!window.confirm(`Envoyer « ${r.name} » au comptable ?`)) return;
    setActing(r.report_id);
    try {
      await api.post(`/admin/reports/${r.report_id}/review`, { decision: "approved" });
      toast.success("Rapport approuvé et envoyé au comptable");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setActing(null);
    }
  };

  const submitReject = async () => {
    const r = rejectCtx?.report;
    if (!r) return;
    setActing(r.report_id);
    try {
      await api.post(`/admin/reports/${r.report_id}/review`, {
        decision: "rejected",
        comment: rejectComment.trim() || null,
      });
      toast.success("Rapport renvoyé à l'employé");
      setRejectCtx(null);
      setRejectComment("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setActing(null);
    }
  };

  const submitToVp = async () => {
    const r = vpCtx?.report;
    if (!r || !vpId) {
      toast.error("Sélectionnez un VP");
      return;
    }
    setActing(r.report_id);
    try {
      await api.post(`/admin/reports/${r.report_id}/send-to-vp`, {
        vp_user_id: vpId,
        note: vpNote.trim() || null,
      });
      toast.success("Rapport envoyé au VP");
      setVpCtx(null);
      setVpId("");
      setVpNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setActing(null);
    }
  };

  const recallFromVp = async (r) => {
    if (!window.confirm(`Annuler la demande d'approbation envoyée au VP pour « ${r.name} » ?\n\nLe rapport reviendra dans la section "À envoyer au VP". Le VP sera notifié de l'annulation.`)) return;
    setActing(r.report_id);
    try {
      await api.post(`/admin/reports/${r.report_id}/recall-from-vp`);
      toast.success("Demande annulée — rapport ré-éditable");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setActing(null);
    }
  };

  const openFiles = (r) => {
    setFilesUser({
      user_id: r.user_id,
      name: r.owner_name,
      email: r.owner_email,
      picture: r.owner_picture,
    });
  };

  const StatusBadge = ({ status }) => {
    const cfg = STATUS_LABELS[status];
    if (!cfg) return null;
    const Ico = cfg.icon;
    const cls = {
      amber: "bg-amber-100 text-amber-800 border-amber-300",
      blue: "bg-blue-100 text-blue-800 border-blue-300",
      green: "bg-green-100 text-green-800 border-green-300",
      red: "bg-red-100 text-red-800 border-red-300",
    }[cfg.color];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold border rounded-full ${cls}`}>
        <Ico size={12} /> {cfg.label}
      </span>
    );
  };

  const renderReport = (r) => (
    <div
      key={r.report_id}
      className={`ra-card p-5 border-l-4 !border-l-${STATUS_LABELS[r.status]?.color || "gray"}-500`}
      data-testid={`pending-report-${r.report_id}`}
    >
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {r.owner_picture
            ? <img src={r.owner_picture} alt="" className="w-12 h-12 rounded-full border border-gray-200 shrink-0" />
            : <div className="w-12 h-12 rounded-full bg-gray-200 shrink-0" />}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <div className="font-heading font-black text-lg tracking-tight">{r.name}</div>
              <StatusBadge status={r.status} />
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {r.owner_name} · <span className="text-gray-500">{r.owner_email}</span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mt-2">
              <span><strong className="num text-gray-900 dark:text-white">{r.invoice_count}</strong> facture(s)</span>
              <span>Total : <strong className="num text-gray-900 dark:text-white">{(r.total_sum || 0).toFixed(2)} $</strong></span>
              {r.month && <span>Mois : {r.month}</span>}
              {r.submitted_at && <span>Soumis le {String(r.submitted_at).slice(0,10)}</span>}
              {r.vp_name && <span>VP : <strong className="text-gray-900 dark:text-white">{r.vp_name}</strong></span>}
            </div>
            {r.vp_note && (
              <div className="mt-3 p-3 bg-red-50 dark:bg-red-950/30 border-l-3 border-red-500 text-sm text-red-900 dark:text-red-200 rounded">
                <strong>Note du VP :</strong> {r.vp_note}
              </div>
            )}
            {r.admin_note_to_vp && (
              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/30 border-l-3 border-blue-500 text-sm text-blue-900 dark:text-blue-200 rounded">
                <strong>Votre note au VP :</strong> {r.admin_note_to_vp}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <button
            className="btn-secondary"
            onClick={() => openFiles(r)}
            data-testid={`review-view-files-${r.report_id}`}
          >
            <FolderOpen size={14} /> Voir les fichiers
          </button>
          {(r.status === "ready_for_review" || r.status === "rejected_by_vp" || r.status === "vp_approved") && (
            <button
              className="btn-primary"
              onClick={() => { setVpCtx({ report: r }); setVpId(r.vp_user_id || ""); setVpNote(""); }}
              disabled={acting === r.report_id || vps.length === 0}
              data-testid={`send-to-vp-${r.report_id}`}
              title={vps.length === 0 ? "Aucun VP configuré — créez d'abord un compte avec rôle 'vp'" : ""}
            >
              <UserCheck size={14} /> {r.status === "vp_approved" ? "Renvoyer au VP" : "Envoyer au VP"}
            </button>
          )}
          {r.status === "pending_vp" && (
            <button
              className="btn-secondary !border-amber-500 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/30"
              onClick={() => recallFromVp(r)}
              disabled={acting === r.report_id}
              data-testid={`recall-from-vp-${r.report_id}`}
              title="Récupérer le rapport (annuler la demande au VP)"
            >
              <Undo2 size={14} /> Annuler envoi VP
            </button>
          )}
          {(r.status === "ready_for_review" || r.status === "rejected_by_vp" || r.status === "vp_approved") && (
            <button
              className="btn-secondary !border-amber-500 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/30"
              onClick={() => { setRejectCtx({ report: r }); setRejectComment(""); }}
              disabled={acting === r.report_id}
              data-testid={`review-reject-${r.report_id}`}
            >
              <Send size={14} /> Renvoyer à l'employé
            </button>
          )}
          {r.status === "vp_approved" && (
            <button
              className="btn-primary !bg-green-600 hover:!bg-green-700"
              onClick={() => approve(r)}
              disabled={acting === r.report_id}
              data-testid={`send-accountant-${r.report_id}`}
            >
              <CheckCircle2 size={14} /> {acting === r.report_id ? "..." : "Envoyer au comptable"}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const Section = ({ title, items, hint }) => (
    items.length > 0 && (
      <div className="mb-8">
        <h2 className="font-heading font-black text-xl tracking-tight mb-2">{title} <span className="text-gray-400 font-normal text-base">({items.length})</span></h2>
        {hint && <p className="text-sm text-gray-500 mb-3">{hint}</p>}
        <div className="grid gap-4">{items.map(renderReport)}</div>
      </div>
    )
  );

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <AdminSubnav />
      <p className="overline mb-2">Administration</p>
      <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter leading-tight sm:leading-none mb-3">
        Rapports à valider
      </h1>
      <p className="text-gray-500 mb-8 max-w-lg">
        Workflow : Employé → Admin → VP → Admin → Comptable. Vous pouvez aussi renvoyer à l'employé pour corrections.
      </p>

      {vps.length === 0 && (
        <div className="ra-card p-4 mb-6 border-l-4 !border-l-amber-500 bg-amber-50 dark:bg-amber-950/30 text-sm">
          <strong>Aucun Vice-président configuré.</strong> Allez dans <a href="/admin" className="underline">Utilisateurs</a> et changez le rôle d'un utilisateur en « Vice-président » avant de pouvoir transférer un rapport.
        </div>
      )}

      {loading ? (
        <div className="ra-card p-8 text-center text-gray-400">Chargement...</div>
      ) : reports.length === 0 ? (
        <div className="ra-card p-12 text-center flex flex-col items-center gap-3">
          <Inbox size={40} className="text-gray-300" />
          <div className="font-heading font-black text-xl">Aucun rapport en attente</div>
          <div className="text-sm text-gray-500">Tous les comptes de dépenses soumis ont été traités.</div>
        </div>
      ) : (
        <div data-testid="pending-reports-list">
          <Section
            title="✅ Approuvé par VP — à envoyer au comptable"
            items={groups.vp_approved}
            hint="Le VP a donné son feu vert. Cliquez « Envoyer au comptable » pour finaliser, ou renvoyez à l'employé/au VP si vous découvrez une erreur."
          />
          <Section
            title="❌ Refusé par VP — à corriger"
            items={groups.rejected_by_vp}
            hint="Le VP a refusé avec une note. Renvoyez à l'employé pour corrections, ou re-soumettez au VP après vérifications."
          />
          <Section
            title="📤 À transférer au VP"
            items={groups.ready_for_review}
            hint="Rapports soumis par les employés. Choisissez un VP et envoyez-lui pour approbation."
          />
          <Section
            title="⏳ En attente du VP"
            items={groups.pending_vp}
            hint="Rapports en cours d'approbation par un Vice-président."
          />
        </div>
      )}

      {filesUser && (
        <AdminUserFilesModal user={filesUser} onClose={() => setFilesUser(null)} />
      )}

      {rejectCtx && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="reject-modal">
          <div className="bg-white dark:bg-neutral-900 border-2 border-[#0B0F19] dark:border-white w-full max-w-md">
            <div className="p-5 border-b border-gray-200 dark:border-neutral-700">
              <p className="overline">Renvoyer à l'employé</p>
              <div className="font-heading font-black text-xl tracking-tight">{rejectCtx.report.name}</div>
              <div className="text-sm text-gray-500 mt-1">{rejectCtx.report.owner_name}</div>
            </div>
            <div className="p-5">
              <label className="ra-label">Notes pour l'employé <span className="text-red-500">*</span></label>
              <textarea
                className="ra-input min-h-[120px]"
                placeholder="Ex: Il manque la facture du 15 mars. Le pourboire de la facture #3 semble incorrect."
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                data-testid="reject-comment"
              />
              <p className="text-xs text-gray-500 mt-2">L'employé recevra un courriel avec vos notes et pourra corriger puis re-soumettre.</p>
            </div>
            <div className="p-5 border-t border-gray-200 dark:border-neutral-700 flex gap-2 justify-end">
              <button className="btn-secondary" onClick={() => { setRejectCtx(null); setRejectComment(""); }}>Annuler</button>
              <button
                className="btn-primary !bg-amber-600 hover:!bg-amber-700"
                onClick={submitReject}
                disabled={acting === rejectCtx.report.report_id || !rejectComment.trim()}
                data-testid="confirm-reject-button"
              >
                <Send size={14} /> Renvoyer à l'employé
              </button>
            </div>
          </div>
        </div>
      )}

      {vpCtx && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="send-to-vp-modal">
          <div className="bg-white dark:bg-neutral-900 border-2 border-[#0B0F19] dark:border-white w-full max-w-md">
            <div className="p-5 border-b border-gray-200 dark:border-neutral-700">
              <p className="overline">Envoyer au Vice-président</p>
              <div className="font-heading font-black text-xl tracking-tight">{vpCtx.report.name}</div>
              <div className="text-sm text-gray-500 mt-1">{vpCtx.report.owner_name}</div>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="ra-label">Choisir un VP <span className="text-red-500">*</span></label>
                <select
                  className="ra-input"
                  value={vpId}
                  onChange={(e) => setVpId(e.target.value)}
                  data-testid="vp-select"
                >
                  <option value="">— Sélectionnez —</option>
                  {vps.map((v) => (
                    <option key={v.user_id} value={v.user_id}>{v.name} — {v.email}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="ra-label">Note au VP (optionnel)</label>
                <textarea
                  className="ra-input min-h-[80px]"
                  placeholder="Ex: Merci de bien vouloir approuver le rapport d'avril."
                  value={vpNote}
                  onChange={(e) => setVpNote(e.target.value)}
                  data-testid="vp-note"
                />
              </div>
              <p className="text-xs text-gray-500">Le VP recevra un courriel avec le PDF + Excel en pièces jointes et un bouton pour ouvrir le rapport.</p>
            </div>
            <div className="p-5 border-t border-gray-200 dark:border-neutral-700 flex gap-2 justify-end">
              <button className="btn-secondary" onClick={() => { setVpCtx(null); setVpId(""); setVpNote(""); }}>Annuler</button>
              <button
                className="btn-primary"
                onClick={submitToVp}
                disabled={acting === vpCtx.report.report_id || !vpId}
                data-testid="confirm-send-to-vp"
              >
                <UserCheck size={14} /> Envoyer au VP
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
